import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ProShell } from "@/components/ProShell";
import { rupee } from "@/lib/constants";
import { Loader2, Wallet, Star, Briefcase, Gift } from "lucide-react";
import { StatementDownload, monthsOf } from "@/components/StatementDownload";
import { PayoutBreakdown } from "@/components/PayoutBreakdown";
import { CarePayouts } from "@/components/CarePayouts";

export default function ProEarnings() {
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/pro/earnings").then((r) => setData(r.data)).catch(() => setData(false)); }, []);

    if (!data) return <ProShell><div className="flex min-h-screen items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div></ProShell>;

    return (
        <ProShell>
            <div className="rounded-b-3xl bg-navy px-5 pb-6 pt-6 text-white">
                <h1 className="font-heading text-2xl font-extrabold">Earnings</h1>
                <p className="text-sm text-white/60">Your HOMIIE payout summary</p>
                <div className="mt-4 rounded-2xl bg-white/10 p-4">
                    <p className="text-xs text-white/60">Total payout (net)</p>
                    <p className="font-heading text-3xl font-extrabold text-orange">{rupee(data.total)}</p>
                </div>
            </div>

            <div className="px-5 py-5">
                <div className="grid grid-cols-3 gap-3">
                    <Stat label="Today" value={rupee(data.today)} />
                    <Stat label="This week" value={rupee(data.week)} />
                    <Stat label="This month" value={rupee(data.month)} />
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                    <IconStat icon={Briefcase} label="Jobs completed" value={data.jobs_completed} />
                    <IconStat icon={Star} label="Rating" value={`${data.rating || "New"} (${data.rating_count})`} />
                    <IconStat icon={Gift} label="Referrals" value={data.referrals_total} />
                    <IconStat icon={Wallet} label="Completed refs" value={data.referrals_completed} />
                </div>

                <CarePayouts />

                <div className="mb-3 mt-6 flex flex-wrap items-center justify-between gap-2">
                    <h2 className="font-heading text-lg font-bold text-navy">Payout history</h2>
                    <StatementDownload months={monthsOf(data.payouts)} testid="pro-statement" accent="#F97316" />
                </div>
                {data.payouts.length === 0 && <p className="text-sm text-navy/40">No payouts yet.</p>}
                <div className="space-y-2.5">
                    {data.payouts.map((p) => (
                        <div key={p.id} className="rounded-2xl border border-navy/10 bg-white p-4" data-testid={`payout-${p.booking_ref}`}>
                            <div className="flex items-center justify-between">
                                <span className="font-heading text-sm font-extrabold text-navy">{p.booking_ref}</span>
                                <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${p.status === "Paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{p.status}</span>
                            </div>
                            <PayoutBreakdown p={p} />
                        </div>
                    ))}
                </div>
            </div>
        </ProShell>
    );
}

function Stat({ label, value }) {
    return <div className="rounded-2xl border border-navy/10 bg-white p-3 text-center"><p className="text-[11px] text-navy/45">{label}</p><p className="mt-0.5 font-heading text-base font-extrabold text-navy">{value}</p></div>;
}
function IconStat({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-3 rounded-2xl border border-navy/10 bg-white p-3.5">
            <div className="rounded-xl bg-orange/10 p-2"><Icon size={18} className="text-orange" /></div>
            <div><p className="text-xs text-navy/45">{label}</p><p className="font-heading font-bold text-navy">{value}</p></div>
        </div>
    );
}
